import { Badge, Button } from "@design";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";

export interface Citation {
  key: string;
  /** The number in the badge, e.g. what the answer cites it as; defaults to the row's position (1, 2, …). */
  number?: number;
  source: string;
  /** Shown next to the name, e.g. the site a web page came from. */
  section?: string;
  /** Who can see the source (e.g. "Team", "Everyone"). */
  scope?: { label: string; icon?: ReactNode; variant?: "info" | "emphasis" };
  snippet?: string;
  /** Formatted, e.g. "updated 2h ago". */
  updated?: string;
  onOpen?: () => void;
  /** Label for the open action; defaults to "Open in Knowledge". */
  openLabel?: string;
}

/**
 * Numbered sources behind an answer; the body of the "N sources searched" Disclosure. Snippets show 3 lines.
 * Several lists can stack in one Disclosure (e.g. "Cited" over "Also consulted"), each named by its heading.
 */
export function CitationList({
  citations,
  heading,
  numbered = true,
}: Readonly<{
  citations: Citation[];
  /** Defaults to "Sources searched for this answer". */
  heading?: string;
  /** Show each row's number (off for sources the answer doesn't cite). */
  numbered?: boolean;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "message" });
  const headingId = useId();
  return (
    <>
      <div
        id={headingId}
        className="border-b border-border-subtle px-3.5 py-2 [font:var(--type-eyebrow)] text-content-tertiary uppercase tracking-wide [&:not(:first-child)]:border-t"
      >
        {heading ?? t("citationsHeading")}
      </div>
      <ol aria-labelledby={headingId}>
        {citations.map((citation, index) => (
          <li
            key={citation.key}
            className={`flex gap-3 px-3.5 py-3 ${index > 0 ? "border-t border-border-subtle" : ""}`}
          >
            {numbered && (
              <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface-secondary text-xs font-bold text-content-primary">
                {citation.number ?? index + 1}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-content-primary">{citation.source}</span>
                {citation.section && <span className="text-xs text-content-tertiary">{citation.section}</span>}
                {citation.scope && (
                  <Badge variant={citation.scope.variant ?? "emphasis"} icon={citation.scope.icon}>
                    {citation.scope.label}
                  </Badge>
                )}
              </div>
              {citation.snippet && (
                <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-content-secondary">{citation.snippet}</p>
              )}
              {(citation.updated || citation.onOpen) && (
                <div className="mt-1.5 flex items-center gap-3 text-xs text-content-tertiary">
                  {citation.updated && <span>{citation.updated}</span>}
                  {citation.onOpen && (
                    <Button variant="link" size="sm" onClick={citation.onOpen}>
                      {citation.openLabel ?? t("openInKnowledge")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}
